"use client";

import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { Check, ChevronDown, Search, X } from "lucide-react";

export type SelectOption = {
  value: string;
  label: string;
  /** Secondary line under the label in the menu. */
  description?: string;
  /** Leading icon in trigger + menu (context-dependent). */
  icon?: ReactNode;
  /** Initials avatar instead of icon (e.g. operators). Optional src for photo. */
  avatar?: { initials: string; src?: string; online?: boolean };
  /** Small type chip on the selected trigger (e.g. "Queue"). */
  badge?: string;
  /** Optional section header when grouping is enabled. */
  group?: string;
  disabled?: boolean;
  /** Subtle trailing mark (e.g. deploying hourglass). */
  suffix?: ReactNode;
};

export type SelectProps = {
  options: SelectOption[];
  value?: string | null;
  onChange: (value: string | null) => void;
  label?: string;
  placeholder?: string;
  /** Show search field in the panel. Default: true when options.length > 8. */
  searchable?: boolean;
  searchPlaceholder?: string;
  clearable?: boolean;
  disabled?: boolean;
  className?: string;
  /** Extra class on the trigger button (e.g. form-control sizing). */
  triggerClassName?: string;
  size?: "sm" | "md";
  ariaLabel?: string;
  emptyText?: string;
  footerHint?: string;
  /** Group menu items by option.group when present. */
  groupBy?: boolean;
  /** Compact chrome for toolbar / filter rows. */
  variant?: "default" | "toolbar";
};

type PanelPos = {
  left: number;
  width: number;
  maxHeight: number;
  placement: "bottom" | "top";
  /** absolute (inside dialog) or fixed (page) */
  mode: "absolute" | "fixed";
  /** offset from container top (bottom placement) */
  top?: number;
  /** offset from container bottom (top placement) */
  bottom?: number;
};

function normalize(s: string) {
  return s.trim().toLowerCase();
}

function matchesQuery(opt: SelectOption, q: string) {
  if (!q) return true;
  const hay = `${opt.label} ${opt.description || ""} ${opt.badge || ""} ${opt.group || ""} ${opt.value}`;
  return normalize(hay).includes(q);
}

export function Select({
  options,
  value = null,
  onChange,
  label,
  placeholder = "Select…",
  searchable,
  searchPlaceholder = "Search…",
  clearable = false,
  disabled = false,
  className = "",
  triggerClassName = "",
  size = "md",
  ariaLabel,
  emptyText = "No matches",
  footerHint,
  groupBy = false,
  variant = "default",
}: SelectProps) {
  const uid = useId();
  const listId = `${uid}-list`;
  const rootRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const searchRef = useRef<HTMLInputElement | null>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [highlight, setHighlight] = useState(0);
  const [panelPos, setPanelPos] = useState<PanelPos | null>(null);
  const [portalEl, setPortalEl] = useState<HTMLElement | null>(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const enableSearch = searchable ?? options.length > 8;
  const selected = useMemo(
    () => options.find((o) => o.value === value) || null,
    [options, value],
  );

  const filtered = useMemo(
    () => options.filter((o) => matchesQuery(o, normalize(query))),
    [options, query],
  );

  const sections = useMemo(() => {
    if (!groupBy) return [{ title: null as string | null, items: filtered }];
    const order: string[] = [];
    const map = new Map<string, SelectOption[]>();
    const ungrouped: SelectOption[] = [];
    for (const opt of filtered) {
      const g = (opt.group || "").trim();
      if (!g) {
        ungrouped.push(opt);
        continue;
      }
      if (!map.has(g)) {
        map.set(g, []);
        order.push(g);
      }
      map.get(g)!.push(opt);
    }
    const out: Array<{ title: string | null; items: SelectOption[] }> = order.map((title) => ({
      title,
      items: map.get(title)!,
    }));
    if (ungrouped.length) out.push({ title: null, items: ungrouped });
    return out;
  }, [filtered, groupBy]);

  const flat = useMemo(() => sections.flatMap((s) => s.items), [sections]);

  const close = useCallback(() => {
    setOpen(false);
    setQuery("");
    setHighlight(0);
    setPanelPos(null);
  }, []);

  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    // Prefer DialogContent host so Radix focus/scroll lock still allows interaction.
    const dialog = trigger.closest("[data-slot='dialog-content']");
    const host = dialog instanceof HTMLElement ? dialog : null;
    setPortalEl(host || document.body);

    const r = trigger.getBoundingClientRect();
    const gap = 6;
    const pad = 8;
    const preferH = Math.min(360, window.innerHeight * 0.5);

    if (host) {
      const hr = host.getBoundingClientRect();
      const spaceBelow = hr.bottom - r.bottom - gap - pad;
      const spaceAbove = r.top - hr.top - gap - pad;
      const placement: "bottom" | "top" =
        spaceBelow >= 200 || spaceBelow >= spaceAbove ? "bottom" : "top";
      const maxHeight = Math.max(
        140,
        Math.min(preferH, placement === "bottom" ? spaceBelow : spaceAbove),
      );
      const width = Math.min(Math.max(r.width, 220), Math.min(420, hr.width - pad * 2));
      let left = r.left - hr.left;
      if (left + width > hr.width - pad) left = hr.width - pad - width;
      if (left < pad) left = pad;
      if (placement === "bottom") {
        setPanelPos({
          mode: "absolute",
          placement,
          top: r.bottom - hr.top + gap,
          left,
          width,
          maxHeight,
        });
      } else {
        setPanelPos({
          mode: "absolute",
          placement,
          bottom: hr.bottom - r.top + gap,
          left,
          width,
          maxHeight,
        });
      }
      return;
    }

    const spaceBelow = window.innerHeight - r.bottom - gap - pad;
    const spaceAbove = r.top - gap - pad;
    const placement: "bottom" | "top" =
      spaceBelow >= 220 || spaceBelow >= spaceAbove ? "bottom" : "top";
    const maxHeight = Math.max(140, Math.min(preferH, placement === "bottom" ? spaceBelow : spaceAbove));
    const width = Math.min(Math.max(r.width, 220), Math.min(420, window.innerWidth - pad * 2));
    let left = r.left;
    if (left + width > window.innerWidth - pad) left = window.innerWidth - pad - width;
    if (left < pad) left = pad;
    if (placement === "bottom") {
      setPanelPos({
        mode: "fixed",
        placement,
        top: r.bottom + gap,
        left,
        width,
        maxHeight,
      });
    } else {
      setPanelPos({
        mode: "fixed",
        placement,
        bottom: window.innerHeight - r.top + gap,
        left,
        width,
        maxHeight,
      });
    }
  }, []);

  const openMenu = useCallback(() => {
    if (disabled) return;
    setOpen(true);
    const idx = Math.max(0, flat.findIndex((o) => o.value === value));
    setHighlight(idx >= 0 ? idx : 0);
  }, [disabled, flat, value]);

  useLayoutEffect(() => {
    if (!open) return;
    updatePosition();
    const onReposition = (e?: Event) => {
      // Scrolling inside the menu must not reposition (resets list scroll → "frozen").
      const t = e?.target;
      if (t instanceof Node && panelRef.current?.contains(t)) return;
      updatePosition();
    };
    window.addEventListener("resize", onReposition);
    // Capture scroll from modal panels / nested overflow (not the menu itself).
    window.addEventListener("scroll", onReposition, true);
    return () => {
      window.removeEventListener("resize", onReposition);
      window.removeEventListener("scroll", onReposition, true);
    };
  }, [open, updatePosition, filtered.length, query, enableSearch, footerHint]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: globalThis.MouseEvent) => {
      const t = e.target as Node | null;
      if (!t) return;
      if (rootRef.current?.contains(t)) return;
      if (panelRef.current?.contains(t)) return;
      close();
    };
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        close();
      }
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, close]);

  useEffect(() => {
    if (!open || !enableSearch) return;
    const t = window.setTimeout(() => searchRef.current?.focus(), 0);
    return () => window.clearTimeout(t);
  }, [open, enableSearch]);

  useEffect(() => {
    setHighlight((h) => (flat.length ? Math.min(h, flat.length - 1) : 0));
  }, [flat.length]);

  function pick(opt: SelectOption) {
    if (opt.disabled) return;
    onChange(opt.value);
    close();
  }

  function clear(e: ReactMouseEvent) {
    e.stopPropagation();
    e.preventDefault();
    onChange(null);
  }

  function onTriggerKey(e: ReactKeyboardEvent<HTMLButtonElement>) {
    if (disabled) return;
    if (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      if (!open) openMenu();
    }
  }

  function onListKey(e: ReactKeyboardEvent<HTMLDivElement>) {
    if (!flat.length) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => (h + 1) % flat.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => (h - 1 + flat.length) % flat.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      const opt = flat[highlight];
      if (opt) pick(opt);
    }
  }

  let flatIndex = -1;
  const hasSelection = Boolean(selected);
  const resultLabel =
    filtered.length === 1 ? "1 result" : `${filtered.length} results`;

  const panelStyle: CSSProperties | undefined = panelPos
    ? {
        position: panelPos.mode,
        left: panelPos.left,
        width: panelPos.width,
        maxWidth: panelPos.width,
        zIndex: 200,
        pointerEvents: "auto",
        ...(panelPos.placement === "bottom"
          ? { top: panelPos.top, bottom: "auto" }
          : { bottom: panelPos.bottom, top: "auto" }),
      }
    : undefined;

  const panel = open && panelPos && mounted ? (
    <div
      ref={panelRef}
      className={[
        "pulse-select-panel",
        "pulse-select-panel--portal",
        panelPos.placement === "top" ? "is-top" : "is-bottom",
        `pulse-select--${size}`,
        `pulse-select--${variant}`,
      ]
        .filter(Boolean)
        .join(" ")}
      style={panelStyle}
      id={listId}
      role="listbox"
      tabIndex={-1}
      aria-label={ariaLabel || label || "Options"}
      onKeyDown={onListKey}
    >
      {enableSearch ? (
        <div className="pulse-select-search">
          <Search size={15} strokeWidth={2.2} aria-hidden="true" />
          <input
            ref={searchRef}
            type="search"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setHighlight(0);
            }}
            placeholder={searchPlaceholder}
            aria-label={searchPlaceholder}
            autoComplete="off"
          />
          {query ? (
            <button
              type="button"
              className="pulse-select-search-clear"
              aria-label="Clear search"
              onClick={() => {
                setQuery("");
                setHighlight(0);
                searchRef.current?.focus();
              }}
            >
              <X size={14} strokeWidth={2.2} />
            </button>
          ) : null}
        </div>
      ) : null}

      {enableSearch ? (
        <div className="pulse-select-count" aria-live="polite">
          {resultLabel}
        </div>
      ) : null}

      <div className="pulse-select-list" style={{ maxHeight: panelPos.maxHeight }}>
        {flat.length === 0 ? (
          <div className="pulse-select-empty">{emptyText}</div>
        ) : (
          sections.map((section, si) => (
            <div key={section.title || `sec-${si}`} className="pulse-select-section">
              {section.title ? (
                <div className="pulse-select-group">{section.title}</div>
              ) : null}
              {section.items.map((opt) => {
                flatIndex += 1;
                const idx = flatIndex;
                const isActive = opt.value === value;
                const isHi = idx === highlight;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    role="option"
                    aria-selected={isActive}
                    disabled={opt.disabled}
                    className={[
                      "pulse-select-option",
                      isActive ? "is-active" : "",
                      isHi ? "is-highlight" : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    onMouseEnter={() => setHighlight(idx)}
                    onClick={() => pick(opt)}
                  >
                    {opt.icon ? (
                      <span className="pulse-select-leading" aria-hidden="true">
                        {opt.icon}
                      </span>
                    ) : opt.avatar ? (
                      <span
                        className={`pulse-select-avatar${opt.avatar.online ? " is-online" : ""}${opt.avatar.src ? " has-img" : ""}`}
                        aria-hidden="true"
                      >
                        {opt.avatar.src ? (
                          <img src={opt.avatar.src} alt="" />
                        ) : (
                          opt.avatar.initials
                        )}
                      </span>
                    ) : (
                      <span className="pulse-select-leading is-spacer" aria-hidden="true" />
                    )}
                    <span className="pulse-select-option-text">
                      <span className="pulse-select-option-label">
                        <span className="pulse-select-option-label-text" title={opt.label}>{opt.label}</span>
                        {opt.suffix ? (
                          <span className="pulse-select-suffix" aria-hidden="true">{opt.suffix}</span>
                        ) : null}
                      </span>
                      {opt.description ? (
                        <span className="pulse-select-option-desc" title={opt.description}>{opt.description}</span>
                      ) : null}
                    </span>
                    {isActive ? (
                      <Check
                        className="pulse-select-check"
                        size={16}
                        strokeWidth={2.4}
                        aria-hidden="true"
                      />
                    ) : (
                      <span className="pulse-select-check-spacer" aria-hidden="true" />
                    )}
                  </button>
                );
              })}
            </div>
          ))
        )}
      </div>

      {footerHint ? (
        <div className="pulse-select-footer">
          <Search size={13} strokeWidth={2.2} aria-hidden="true" />
          <span>{footerHint}</span>
        </div>
      ) : null}
    </div>
  ) : null;

  return (
    <div
      ref={rootRef}
      className={[
        "pulse-select",
        `pulse-select--${size}`,
        `pulse-select--${variant}`,
        open ? "is-open" : "",
        hasSelection ? "is-selected" : "",
        disabled ? "is-disabled" : "",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {label ? (
        <label className="pulse-select-label" htmlFor={`${uid}-trigger`}>
          {label}
        </label>
      ) : null}

      <button
        type="button"
        ref={triggerRef}
        id={`${uid}-trigger`}
        className={["pulse-select-trigger", triggerClassName].filter(Boolean).join(" ")}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        aria-label={ariaLabel || label || placeholder}
        disabled={disabled}
        onClick={() => (open ? close() : openMenu())}
        onKeyDown={onTriggerKey}
      >
        <span className="pulse-select-trigger-main">
          {selected?.icon ? (
            <span className="pulse-select-leading" aria-hidden="true">
              {selected.icon}
            </span>
          ) : selected?.avatar ? (
            <span
              className={`pulse-select-avatar${selected.avatar.online ? " is-online" : ""}${selected.avatar.src ? " has-img" : ""}`}
              aria-hidden="true"
            >
              {selected.avatar.src ? (
                <img src={selected.avatar.src} alt="" />
              ) : (
                selected.avatar.initials
              )}
            </span>
          ) : null}
          <span
            className={`pulse-select-value${selected ? "" : " is-placeholder"}`}
            title={selected ? selected.label : placeholder || undefined}
          >
            {selected ? selected.label : placeholder}
          </span>
          {selected?.suffix ? (
            <span className="pulse-select-suffix" aria-hidden="true">{selected.suffix}</span>
          ) : null}
          {selected?.badge ? (
            <span className="pulse-select-badge">{selected.badge}</span>
          ) : null}
        </span>
        <span className="pulse-select-trigger-actions">
          {clearable && selected ? (
            <span
              className="pulse-select-clear"
              role="button"
              tabIndex={-1}
              aria-label="Clear selection"
              onClick={clear}
              onKeyDown={() => {}}
            >
              <X size={14} strokeWidth={2.2} />
            </span>
          ) : null}
          <ChevronDown
            className="pulse-select-chevron"
            size={16}
            strokeWidth={2.2}
            aria-hidden="true"
          />
        </span>
      </button>

      {mounted && panel && portalEl ? createPortal(panel, portalEl) : null}
    </div>
  );
}
